/**
 * EntityCard — interactive character/scene card for the story creation card stage.
 * Wraps ImageItemCard with press-to-focus, regenerate, and loading/error states.
 */

import React from "react";
import { View, Text, Image, Pressable, StyleSheet, Platform } from "react-native";
import { colors, textStyle } from "../../styles";
import { useStoryStore } from "../../store/storyStore";
import { useSocketStore } from "../../store/socketStore";
import ImageItemCard from "./ImageItemCard";

interface EntityCardProps {
  entity: {
    type: "character" | "scene";
    entityId: string;
    name: string;
    visualDescription: string;
    imageBase64?: string;
    isLoading?: boolean;
    hasError?: boolean;
    isModerated?: boolean;
  };
  isFocused?: boolean;
}

export default function EntityCard({ entity, isFocused }: EntityCardProps) {
  const handlePress = () => {
    useStoryStore.getState().setManualFocus(entity.type, entity.entityId);
  };

  const handleRegen = () => {
    const store = useStoryStore.getState();
    if (entity.type === "character") {
      store.updateCharacter(entity.entityId, { isLoading: true, imageBase64: undefined });
    } else {
      store.updateScene(entity.entityId, { isLoading: true, imageBase64: undefined });
    }
    useSocketStore.getState().emitEvent("regenerateElementImage", {
      elementType: entity.type,
      elementId: entity.entityId,
    });
  };

  return (
    <Pressable onPress={handlePress}>
      <ImageItemCard
        label={entity.name}
        style={[isFocused && styles.cardFocused, entity.isModerated && styles.cardModerated]}
      >
        {entity.imageBase64 ? (
          <Image
            source={{ uri: `data:image/jpeg;base64,${entity.imageBase64}` }}
            style={styles.image}
            resizeMode="cover"
            resizeMethod="resize"
          />
        ) : entity.isModerated ? (
          <View style={styles.placeholder}>
            <Text style={{ fontSize: 28 }}>🚫</Text>
          </View>
        ) : entity.isLoading ? (
          <View style={styles.placeholder}>
            <Text style={{ fontSize: 28 }}>⏳</Text>
          </View>
        ) : entity.hasError ? (
          <View style={styles.placeholder}>
            <Text style={{ fontSize: 28 }}>⚠️</Text>
          </View>
        ) : (
          <View style={styles.placeholder}>
            <Text style={{ fontSize: 34 }}>{entity.type === "character" ? "👤" : "🏔"}</Text>
          </View>
        )}
        {isFocused && !entity.isLoading && !entity.isModerated && (
          <Pressable style={styles.regenBtn} onPress={handleRegen}>
            <Text style={styles.regenBtnText}>↻</Text>
          </Pressable>
        )}
      </ImageItemCard>
    </Pressable>
  );
}

const shadowFocused = Platform.OS === "web"
  ? { boxShadow: "0 5px 10px rgba(0,0,0,0.2), 0 0 30px rgba(0,0,0,0.2)" }
  : { shadowColor: colors.black, shadowOffset: { width: 0, height: 5 }, shadowOpacity: 0.2, shadowRadius: 30, elevation: 12 };

const styles = StyleSheet.create({
  cardFocused: {
    ...shadowFocused,
  } as any,
  cardModerated: {
    borderWidth: 3,
    borderColor: "#EF4444",
    opacity: 0.7,
  },
  image: {
    width: "100%",
    height: "100%",
  },
  placeholder: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.surfaceHover,
  },
  regenBtn: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.3)",
    borderRadius: 8,
  },
  regenBtnText: {
    fontSize: 13,
    ...textStyle.heading,
    color: colors.white,
  },
});
